type SentryModule = typeof import('@sentry/react');

let modulePromise: Promise<SentryModule> | null = null;
let initialized = false;
let replayConsent = typeof window !== 'undefined'
  && localStorage.getItem('fairday_cookie_consent') === 'granted';

// Minimal structural type: works for Sentry events and test fixtures alike.
interface ScrubbableRequest {
  headers?: Record<string, unknown>;
  data?: unknown;
}

export const scrubSentryEvent = <T extends { request?: ScrubbableRequest }>(event: T): T => {
  if (event.request?.headers) {
    for (const key of ['Authorization', 'authorization', 'Cookie', 'cookie']) delete event.request.headers[key];
  }
  if (event.request?.data) {
    const sensitiveFields = ['password', 'token', 'secret', 'ssn', 'credit_card', 'api_key', 'refresh_token', 'access_token'];
    try {
      const data = (
        typeof event.request.data === 'string' ? JSON.parse(event.request.data) : event.request.data
      ) as Record<string, unknown>;
      for (const field of sensitiveFields) if (data?.[field]) data[field] = '[FILTERED]';
      event.request.data = data;
    } catch {
      event.request.data = '[FILTERED]';
    }
  }
  return event;
};

const load = async (): Promise<SentryModule> => {
  modulePromise ||= import('@sentry/react');
  const Sentry = await modulePromise;
  if (!initialized) {
    initialized = true;
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      release: `fairday-consumer@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
      enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
      tracesSampleRate: import.meta.env.PROD ? 0.2 : 1,
      replaysSessionSampleRate: replayConsent ? 0.1 : 0,
      replaysOnErrorSampleRate: replayConsent ? 1 : 0,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
      ],
      beforeSend: scrubSentryEvent,
    });
  }
  return Sentry;
};

export const initializeSentry = (): void => { void load(); };
export const captureException = (error: unknown, context?: Record<string, unknown>): void => {
  void load().then(Sentry => Sentry.captureException(error, context));
};
export const setUser = (user: { id?: string; email?: string } | null): void => {
  void load().then(Sentry => Sentry.setUser(user));
};
export const setReplayConsent = (granted: boolean): void => {
  replayConsent = granted;
  void load().then(Sentry => {
    // Replay sample-rate options live on BrowserOptions, but getOptions()
    // returns the narrower ClientOptions — widen with a type-only cast.
    const options = Sentry.getClient()?.getOptions() as
      | { replaysSessionSampleRate?: number; replaysOnErrorSampleRate?: number }
      | undefined;
    if (!options) return;
    options.replaysSessionSampleRate = granted ? 0.1 : 0;
    options.replaysOnErrorSampleRate = granted ? 1 : 0;
  });
};
